// feature/tipo-fallo-tabs.
// Pill-style tab bar to slice a list of fallo cases into Favorables /
// Desfavorables / Nulidad / Sin clasificar buckets. Used by:
//   * Fallos.js (Molly's queue, always visible — replaces the old
//     "Tipo de fallo" dropdown).
//   * CasesList.js (Angie, conditional — only visible when she
//     filters by Tipo de actuación = Fallo).
//
// The DB stores tipo_fallo as the legacy uppercase enum
// (FAVORABLE_1A / DESFAVORABLE_1A / FAVORABLE_2A / DESFAVORABLE_2A /
// NULIDAD / NO_PEDIR_INSUMO). The tabs collapse the 1ª/2ª instancia
// detail into a single bucket per resultado — that's what Molly +
// Angie asked for. NO_PEDIR_INSUMO maps to "Sin clasificar" since
// it's a non-decision marker.

import React, { useMemo } from "react";

const TIPO_FALLO_OPTIONS = [
  { key: "todos",          label: "Todos" },
  { key: "favorable",      label: "Favorables" },
  { key: "desfavorable",   label: "Desfavorables" },
  { key: "nulidad",        label: "Nulidad" },
  { key: "sin_clasificar", label: "Sin clasificar" },
];

// DB enum → tab bucket key.
const TIPO_FALLO_GROUP = {
  FAVORABLE_1A:    "favorable",
  FAVORABLE_2A:    "favorable",
  DESFAVORABLE_1A: "desfavorable",
  DESFAVORABLE_2A: "desfavorable",
  NULIDAD:         "nulidad",
  NO_PEDIR_INSUMO: "sin_clasificar",
};

function bucketOf(c) {
  if (!c) return "sin_clasificar";
  if (!c.tipo_fallo) return "sin_clasificar";
  return TIPO_FALLO_GROUP[c.tipo_fallo] || "sin_clasificar";
}

export function filterByTipoFallo(cases, tabKey) {
  if (!tabKey || tabKey === "todos") return cases;
  return cases.filter((c) => bucketOf(c) === tabKey);
}

export default function TipoFalloTabs({ cases, value, onChange }) {
  const counts = useMemo(() => {
    const c = { todos: cases.length, favorable: 0, desfavorable: 0, nulidad: 0, sin_clasificar: 0 };
    for (const row of cases) {
      const b = bucketOf(row);
      if (c[b] != null) c[b] += 1;
    }
    return c;
  }, [cases]);

  return (
    <div role="tablist" style={{
      display: "flex", gap: 6, flexWrap: "wrap",
      margin: "8px 0 12px",
    }}>
      {TIPO_FALLO_OPTIONS.map((opt) => {
        const count = counts[opt.key] || 0;
        const active = value === opt.key;
        // Hide buckets with zero count except "Todos" and the active tab
        // (so the user can see they're still on a bucket that emptied out).
        if (count === 0 && opt.key !== "todos" && !active) return null;
        return (
          <button key={opt.key} role="tab" type="button"
                  aria-selected={active}
                  onClick={() => onChange(opt.key)}
                  className={active ? "" : "secondary"}
                  style={{
                    padding: "4px 12px", fontSize: 12,
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}>
            <span>{opt.label}</span>
            <span style={{
              background: active ? "rgba(255,255,255,0.25)" : "var(--bg-subtle)",
              color: active ? "var(--on-accent)" : "var(--text-muted)",
              fontSize: 10, fontWeight: 600,
              padding: "1px 6px", borderRadius: 10,
              minWidth: 16, textAlign: "center",
            }}>{count}</span>
          </button>
        );
      })}
    </div>
  );
}
